import React, { useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import SettingsModal from '../../clientSettings/SettingsModal';
import Toggle from '../../Toggle';
import { toast } from '../../Toast';
import { SelectBox } from '../shared';
import { ALL_TIME, ContactMessageItem } from './contactData';

interface Props {
    // the rows the list is showing now, and how many messages that filter matches in total
    rows: ContactMessageItem[];
    viewCount: number;
    allRows: ContactMessageItem[];
    onClose: () => void;
}

const FORMATS = ['Excel (.xlsx)', 'CSV (.csv)'];
const RANGES = ['Last 12 months', 'Last 30 days', 'Last 90 days', 'All time'];

const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;

// preview: the sample rows as a CSV (no .xlsx writer in the app yet)
const downloadCsv = (rows: ContactMessageItem[], full: boolean) => {
    const head = ['Name', 'Email', 'Subject', 'Form', 'Received', 'Status', ...(full ? ['Message'] : [])];
    const lines = rows.map(m => [m.name, m.email, m.subject, m.form, m.received, m.status, ...(full ? [m.body] : [])].map(cell).join(','));
    const blob = new Blob([[head.map(cell).join(','), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'contact-messages.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// export contact-form messages as a spreadsheet (spec 410:13828)
const ExportMessagesModal: React.FC<Props> = ({ rows, viewCount, allRows, onClose }) => {
    const [scope, setScope] = useState<'view' | 'all'>('view');
    const [format, setFormat] = useState(FORMATS[0]);
    const [range, setRange] = useState(RANGES[0]);
    const [full, setFull] = useState(true);

    const choices = [
        { value: 'view' as const, title: `Current view (${viewCount} messages)`, hint: 'Matches the filters you have on now' },
        { value: 'all' as const, title: `Everything (${ALL_TIME.count} messages since ${ALL_TIME.since})` },
    ];

    const download = () => {
        downloadCsv(scope === 'view' ? rows : allRows, full);
        toast.info('Preview only: downloaded the sample messages on this page as a CSV');
        onClose();
    };

    return <SettingsModal icon={<FiDownload />} title="Export Messages" sub="Download a spreadsheet of contact form messages" onClose={onClose}
        footer={<button type="button" className="btn-primary with-icon" onClick={download}><FiDownload /> Download</button>}>
        <div className="options" role="radiogroup" aria-label="What to export">
            {choices.map(c => <button key={c.value} type="button" role="radio" aria-checked={scope === c.value}
                className={`option${scope === c.value ? ' selected' : ''}`} onClick={() => setScope(c.value)}>
                <span className="radio" />
                <span className="text"><strong>{c.title}</strong>{c.hint && <span>{c.hint}</span>}</span>
            </button>)}
        </div>

        <div className="fields">
            <div className="field">
                <span className="label">Format</span>
                <SelectBox className="hub-select-full" label="Format" value={format} options={FORMATS} onChange={setFormat} />
            </div>
            <div className="field">
                <span className="label">Date range</span>
                <SelectBox className="hub-select-full" label="Date range" value={range} options={RANGES} onChange={setRange} />
            </div>
        </div>

        <div className="hub-export-full">
            <div className="hub-export-text"><strong>Include the full message text</strong><span>Off = name, email, subject and date only</span></div>
            <Toggle checked={full} onChange={setFull} label="Include the full message text" />
        </div>
    </SettingsModal>;
};

export default ExportMessagesModal;
